/* eslint-disable @typescript-eslint/no-explicit-any */
import { WORDPRESS_API_URL } from '../config';

const baseURL = WORDPRESS_API_URL;

// Helper function for all API calls
const fetchFromWordPress = async (url: string): Promise<Response> => {
  return fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export interface BlogPost {
  id: number;
  author: number;
  categories: number[];
  class_list: string[];
  comment_status: string;
  content: {
    rendered: string;
    protected?: boolean;
  };
  date: string;
  date_gmt: string;
  excerpt: {
    rendered: string;
    protected?: boolean;
  };
  featured_media: number;
  format: string;
  guid: {
    rendered: string;
  };
  link: string;
  meta: Record<string, any>;
  modified: string;
  modified_gmt: string;
  ping_status: string;
  slug: string;
  status: string;
  sticky: boolean;
  tags: number[];
  template: string;
  title: {
    rendered: string;
  };
  type: string;
  yoast_head?: string;
  yoast_head_json?: Record<string, any>;
  _links?: Record<string, any>;
  _embedded?: {
    author?: any[];
    'wp:featuredmedia'?: any[];
    'wp:term'?: any[];
  };
}

export interface PaginatedResponse {
  posts: BlogPost[];
  total: number;
  totalPages: number;
}

export interface BlogCategory {
  id: number;
  name: string;
}

export const shopperCategoryIds = [23, 114, 47, 119];
const excludedCategories = new Set(['uncategorized', 'wordpress']);
const htmlEntities: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#039;': "'" };
const decodeHtmlEntities = (html: string) => html.replace(/&(?:amp|lt|gt|quot|#039);/g, (entity) => htmlEntities[entity] ?? entity);

export const fetchBlogCategories = async (): Promise<BlogCategory[]> => {
  const apiBase = baseURL.substring(0, baseURL.lastIndexOf('/'));
  const url = `${apiBase}/categories?per_page=100`;
  const response = await fetchFromWordPress(url);

  if (!response.ok) {
    throw new Error('Failed to fetch blog categories');
  }

  const data: Array<{ id: number; name: string }> = await response.json();

  return data
    .filter((category) => !excludedCategories.has(category.name.toLowerCase()))
    .map((category) => ({ id: category.id, name: decodeHtmlEntities(category.name) }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const fetchRecentPosts = async (
  page: number = 1,
  perPage: number = 6,
  tag?: string,
  filter?: boolean,
  categoryIds?: number[],
): Promise<PaginatedResponse> => {
  const tagParam = tag ? `&tags=${tag}` : '';
  const filterParam = categoryIds && categoryIds.length > 0
      ? `&categories=${categoryIds.join(',')}`
      : filter
        ? `&categories=${shopperCategoryIds.join(',')}`
        : '';

  const url = `${baseURL}?_embed${filterParam}&per_page=${perPage}&page=${page}${tagParam}`;

  const response = await fetchFromWordPress(url);

  if (!response.ok) {
    throw new Error('Failed to fetch recent posts');
  }

  const data: BlogPost[] = await response.json();
  const total = parseInt(response.headers.get('X-WP-Total') || '0', 10);
  const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);

  return {
    posts: data,
    total,
    totalPages,
  };
};

export const fetchPostsBySlug = async (slug: string): Promise<PaginatedResponse> => {
  const url = `${baseURL}?_embed&slug=${slug}`;

  const response = await fetchFromWordPress(url);

  if (!response.ok) {
    throw new Error('Failed to fetch post');
  }

  const data: BlogPost[] = await response.json();

  return {
    posts: data[0] ? [data[0]] : [],
    total: data.length,
    totalPages: 1,
  };
};

export const fetchPostById = async (id: number): Promise<BlogPost | null> => {
  const url = `${baseURL}/${id}?_embed`;

  const response = await fetchFromWordPress(url);

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error('Failed to fetch post');
  }

  const data: BlogPost = await response.json();

  return data;
};

export const fetchRelatedPosts = async (): Promise<PaginatedResponse> => {
  const response = await fetchFromWordPress(`${baseURL}?_embed`);

  if (!response.ok) {
    throw new Error('Failed to fetch related posts');
  }

  const data: BlogPost[] = await response.json();

  return {
    posts: data,
    total: data.length,
    totalPages: 1,
  };
};

export const fetchRelatedPostsByCategory = async (
  categoryId: number,
  excludePostId?: number,
  perPage: number = 2,
  tag?: string,
): Promise<PaginatedResponse> => {
  let url = `${baseURL}?_embed&categories=${categoryId}&per_page=${perPage}`;

  if (excludePostId) {
    url += `&exclude=${excludePostId}`;
  }
  if (tag) {
    url += `&tags=${tag}`;
  }

  const response = await fetchFromWordPress(url);

  if (!response.ok) {
    throw new Error('Failed to fetch related posts');
  }

  const data: BlogPost[] = await response.json();
  const total = parseInt(response.headers.get('X-WP-Total') || '0', 10);
  const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '0', 10);

  return {
    posts: data,
    total,
    totalPages,
  };
};
